import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http'
import { inject } from '@angular/core'
import { NavController } from '@ionic/angular/standalone'
import { catchError, EMPTY, finalize, Observable, throwError, timeout } from 'rxjs'
import { LoaderService } from '../services/loader.service'
import { SessionActionsService } from '@shared/services/session-actions.service'
import { ToastService } from '@shared/services/toast.service'
import { CrashlyticsService } from '@shared/services/crashlithycs.service'

export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const loaderService = inject(LoaderService)
  const navController = inject(NavController)
  const sessionActionsService = inject(SessionActionsService)
  const toastService = inject(ToastService)
  const crashlyticsService = inject(CrashlyticsService)

  const isFileUpload = req.headers.has('X-File-Upload') || req.headers.has('X-Extend-Timeout')
  const skipTimer = req.headers.has('X-Skip-Timeout')
  const timeoutValue = isFileUpload ? 30000 : 15000

  loaderService.showLoading(timeoutValue)

  return next(req).pipe(
    source$ => {
      return skipTimer ? source$ : source$.pipe(timeout(timeoutValue))
    },
    catchError((error: HttpErrorResponse) => {
      if (!navigator.onLine) {
        toastService.showToast('No hay conexión a internet')
        return throwError(() => new Error('No hay conexión a internet'))
      }

      if (error.status === 403) {
        if (error.error.errorCodeMessage == 'DUROTAN_API_JWT002') {
          navController.navigateBack('solventa/terms', { animated: true, replaceUrl: true })
          return EMPTY
        }

        if (error.error.errorCodeMessage == 'DUROTAN_API_JWT003') {
          navController.navigateBack('solventa/phone/send-code', { animated: true, replaceUrl: true })
          return EMPTY
        }
      }

      if (error.status == 404) {
        if (req.method === 'POST' && req.url.includes('invitation')) {
          navController.navigateForward('solventa/invitation/request', { animated: true, replaceUrl: true })
          toastService.showToast('Solicita un código de invitación')
        }
        return EMPTY
      }

      if (error.status == 400) {
        if (req.method === 'POST' && req.url.includes('invitation/request')) {
          navController.navigateForward('solventa/invitation/sent', { animated: true, replaceUrl: true })
          toastService.showToast('Ya se ha enviado una solicitud de invitación')
        }

        if (req.method === 'GET' && req.url.includes('state')) {
          sessionActionsService.logOut()
        }
        return EMPTY
      }

      if (error.status === 401) {
        if (req.method === 'POST' && req.url.includes('auth/logout')) {
          sessionActionsService.logOut(false)
        } else {
          sessionActionsService.logOut()
        }
        return EMPTY
      }

      if (error.status === 409) {
        if (req.method === 'POST' && req.url.includes('payment-receipts')) {
          toastService.showToast('Tu recibo de pago está siendo procesado, por favor espera')
          navController.navigateForward('solventa/home/active-loan', { animated: true, replaceUrl: true })
        }

        return EMPTY
      }

      // crashlyticsService.recordException(JSON.stringify(error))
      try {
        crashlyticsService.recordException(
          JSON.stringify({
            status: error.status,
            message: error.message,
            url: error.url ?? req.url,
            error: typeof error.error === 'string' ? error.error : undefined,
          }),
        )
      } catch {
        void 0
      }

      return throwError(() => error)
    }),
    finalize(async () => {
      await loaderService.hideLoading()
    }),
  )
}
